import { useEffect, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AuthPageLayout from '../components/AuthPageLayout';

const PARAMS = ['client_id', 'redirect_uri', 'code_challenge', 'code_challenge_method', 'response_type'] as const;

interface Asking {
    client_name: string;
    user_name: string;
    workspace: string;
}

// Where an AI assistant (Claude, ChatGPT…) asks to connect to Navaris through the MCP
// server: the signed-in person allows or refuses it, and is sent back to the assistant.
export default function ConnectPage() {
    const { isAuthenticated, authFetch } = useAuth();
    const location = useLocation();
    const [params] = useSearchParams();
    const [asking, setAsking] = useState<Asking | null>(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const query = new URLSearchParams(PARAMS.map(k => [k, params.get(k) ?? '']));

    useEffect(() => {
        if (!isAuthenticated) return;
        authFetch(`/oauth/client?${query}`)
            .then(async r => {
                if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'This connection request is not valid.');
                return r.json();
            })
            .then(setAsking)
            .catch(e => setError(e.message));
        // The request is fixed by the address; it is checked once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, authFetch]);

    if (!isAuthenticated) {
        return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
    }

    const decide = async (allow: boolean) => {
        setBusy(true);
        setError('');
        try {
            const res = await authFetch('/oauth/approve', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...Object.fromEntries(query), state: params.get('state'), allow }),
            });
            if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Could not complete the connection.');
            window.location.href = (await res.json()).redirect;
        } catch (e: any) {
            setError(e.message);
            setBusy(false);
        }
    };

    return (
        <AuthPageLayout
            title={asking ? `Connect ${asking.client_name} to Navaris` : 'Connect an AI assistant'}
            error={error}
        >
            {!asking && !error && <Loader2 className="w-6 h-6 animate-spin text-brand mx-auto" />}
            {asking && (
                <div className="space-y-5">
                    <p className="text-sm text-graphite">
                        {asking.client_name} is asking to use Navaris as <b>{asking.user_name}</b>
                        {asking.workspace && <> in <b>{asking.workspace}</b></>}. It will be able to:
                    </p>
                    <ul className="text-sm text-coolslate list-disc pl-5 space-y-1">
                        <li>read your company record: its companies and people, figures, plans and tasks</li>
                        <li>ask Navaris for reviews and advice, and to record what you tell it</li>
                    </ul>
                    <p className="text-[13px] text-coolslate">
                        What it reads goes into your conversations with {asking.client_name}. You can see each
                        conversation in Navaris under Agents.
                    </p>
                    <div className="flex gap-3">
                        <button
                            onClick={() => decide(true)}
                            disabled={busy}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-brand text-white font-medium hover:bg-brand-dark disabled:opacity-40"
                        >
                            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Allow
                        </button>
                        <button
                            onClick={() => decide(false)}
                            disabled={busy}
                            className="flex-1 px-4 py-2.5 rounded-lg border border-softgrey text-graphite font-medium hover:bg-offwhite disabled:opacity-40"
                        >
                            Don't allow
                        </button>
                    </div>
                </div>
            )}
        </AuthPageLayout>
    );
}
